import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { ActiveModulesComponent } from 'src/app/_components/chips/active-modules/active-modules.component';
import { ExperimentTemplateListItem } from 'src/app/_models/experiment-template-list-item';
import {
  ExperimentMode,
  ExperimentModeDisplay,
} from 'src/app/_models/experiment-mode';
import { Laboratory } from 'src/app/_models/laboratory';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { MatIcon } from '@angular/material/icon';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { Module } from 'src/app/_models/module-enum';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { FormBase } from 'src/app/_models/form-base';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ExperimentTemplateService } from 'src/app/_services/experiment-template.service';
import { ErrorObject } from 'src/app/_models/error';
import { clearFormArray } from 'src/app/_helpers/utils';
import { MatFabButtonComponent } from 'src/app/_components/buttons/mat-fab-button/mat-fab-button.component';
import { EDIT_ACCESS_ROLE_LIST, Role } from 'src/app/_models/role';
import { User } from 'src/app/_models/user';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { SearchFieldComponent } from 'src/app/_components/forms/search-field/search-field.component';
import { ModalService } from 'src/app/_services/modal.service';
import { FilterModalComponent } from 'src/app/_components/modals/filter-modal/filter-modal.component';
import { ValidationModalComponent } from 'src/app/_components/modals/validation-modal/validation-modal.component';
import { StatusIndicatorComponent } from 'src/app/_components/chips/status-indicator/status-indicator.component';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import {
  canAccessLabData,
  orderModules,
  resolveLabOwnership,
} from '../../_helpers/utils';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { ToastComponent } from 'src/app/_components/toast/toast.component';

@Component({
  selector: 'app-template-list',
  imports: [
    MatTableModule,
    MatIcon,
    ActiveModulesComponent,
    MatIconButtonComponent,
    MatOutlinedButtonComponent,
    LoadingSpinnerComponent,
    IconButtonComponent,
    DropdownFormFieldComponent,
    MatFabButtonComponent,
    RoleCheckPipe,
    SearchFieldComponent,
    FilterModalComponent,
    ValidationModalComponent,
    StatusIndicatorComponent,
    ModalComponent,
    ErrorPageComponent,
    ToastComponent,
  ],
  templateUrl: './template-list.component.html',
  styleUrl: './template-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateListComponent implements OnInit, OnDestroy {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly experimentTemplateService = inject(
    ExperimentTemplateService,
  );
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  readonly ExperimentModeDisplay = ExperimentModeDisplay;
  readonly Role = Role;
  readonly editAccessRoles = EDIT_ACCESS_ROLE_LIST;

  readonly displayedColumns = [
    'name',
    'mode',
    'modules',
    'description',
    'createdBy',
    'actions',
  ];

  private readonly unsubscribe$ = new Subject<void>();
  private successToast?: ReturnType<typeof setTimeout>;

  laboratory = signal<Laboratory | null>(null);
  currentUser = signal<User | null>(null);
  selectedTemplateId = signal<number | null>(null);

  loading = signal<boolean>(false);
  processing = signal<boolean>(false);
  fetchTemplateError = signal<ErrorObject | null>(null);
  deleteTemplateError = signal<ErrorObject | null>(null);
  selectedModuleList = signal<Module[]>([]);
  templates = signal<ExperimentTemplateListItem[]>([]);
  displayToast = signal<boolean>(false);

  // Pagination
  pageRequested = signal<number>(1);
  pageSize = signal<number>(5);
  totalCount = signal<number>(0);
  totalPages = signal<number>(0);
  currentPage = signal<number>(1);
  hasNext = signal<boolean>(false);
  hasPrevious = signal<boolean>(false);

  currentSort = signal<{ sortBy: string; sortDirection: string }>({
    sortBy: 'Name',
    sortDirection: 'asc',
  });

  templatesPerPageField = new FormBase<string>({
    key: 'templatesPerPage',
    label: 'Templates per page',
    type: 'dropdown',
    placeholder: '',
    disabled: false,
    required: false,
    value: '5',
    options: [
      { key: '5', value: '5' },
      { key: '10', value: '10' },
      { key: '25', value: '25' },
      { key: '50', value: '50' },
      { key: '100', value: '100' },
    ],
  });

  experimentModeFilters = new FormBase({
    key: 'experimentModeFilters',
    label: 'Experiment Mode',
    type: 'dropdown',
    placeholder: 'Select...',
    disabled: false,
    required: false,
    value: null,
    options: [
      { key: null, value: 'All' },
      {
        key: ExperimentMode.BatchCulture,
        value: ExperimentModeDisplay[ExperimentMode.BatchCulture],
      },
      {
        key: ExperimentMode.Turbidostat,
        value: ExperimentModeDisplay[ExperimentMode.Turbidostat],
      },
      {
        key: ExperimentMode.Chemostat,
        value: ExperimentModeDisplay[ExperimentMode.Chemostat],
      },
      {
        key: ExperimentMode.PHControl,
        value: ExperimentModeDisplay[ExperimentMode.PHControl],
      },
      {
        key: ExperimentMode.PHControlDouble,
        value: ExperimentModeDisplay[ExperimentMode.PHControlDouble],
      },
      {
        key: ExperimentMode.PHControlChemostat,
        value: ExperimentModeDisplay[ExperimentMode.PHControlChemostat],
      },
      {
        key: ExperimentMode.Morbidostat,
        value: ExperimentModeDisplay[ExperimentMode.Morbidostat],
      },
    ],
  });

  experimentModuleFilters = new FormBase({
    key: 'experimentModuleFilters',
    label: 'Installed modules:',
    type: 'checkbox',
    placeholder: '',
    disabled: false,
    required: false,
    value: '',
    options: [
      { key: Module.Fluorescence, value: 'Fluorescence' },
      { key: Module.LiquidControl, value: 'Liquid Control' },
      { key: Module.Oxygen, value: 'Oxygen' },
      { key: Module.pH, value: 'pH' },
      { key: Module.Sparging, value: 'Sparging' },
    ],
  });

  searchField = new FormBase<string>({
    key: 'search',
    label: 'Search',
    type: 'text',
    placeholder: 'Search...',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  includeDeletedTemplatesField = new FormBase<boolean>({
    key: 'includeDeletedTemplates',
    label: 'Deleted templates',
    type: 'toggle',
    placeholder: '',
    disabled: false,
    required: false,
    value: false,
    options: [],
  });

  templateTableControls = this.formBuilder.group({
    [this.experimentModeFilters.key]: new FormControl(
      this.experimentModeFilters.value,
    ),
    [this.experimentModuleFilters.key]: new FormArray([]),
    [this.searchField.key]: new FormControl(this.searchField.value),
    [this.templatesPerPageField.key]: new FormControl(this.templatesPerPageField.value),
    [this.includeDeletedTemplatesField.key]: new FormControl(
      this.includeDeletedTemplatesField.value,
    ),
  });

  ngOnInit(): void {
    this.initialiseCheckboxes();
    this.currentUser.set(this.authenticationService.currentUserValue);
    // Resolve isOwnLab from the authoritative currentUser.laboratories list,
    // since the lab from selectedLaboratory may not have it populated.
    const resolvedLab = resolveLabOwnership(
      this.authenticationService.selectedLaboratory,
      this.currentUser()?.laboratories,
    );
    // Super Users can view any lab but only own labs have accessible experiment
    // templates. Resolve to null for foreign labs so the template shows the
    // "You are not a member of this lab" page and the initial fetch is skipped.
    const lab = canAccessLabData(resolvedLab, this.currentUser()?.role)
      ? resolvedLab
      : null;
    this.laboratory.set(lab);
    this.setupSubscriptions();
    if (lab) {
      this.getExperimentTemplates();
    }
  }

  setupSubscriptions(): void {
    // Re-fetch templates when the selected lab changes, using the same
    // resolved isOwnLab as the initial guard. Super Users can view any lab but
    // only own labs have accessible experiment templates, so treat a foreign
    // lab as null to show the "You are not a member of this lab" page and skip the fetch.
    this.authenticationService.selectedLab$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((laboratory) => {
        const resolvedLab = resolveLabOwnership(
          laboratory,
          this.currentUser()?.laboratories,
        );
        const lab = canAccessLabData(resolvedLab, this.currentUser()?.role)
          ? resolvedLab
          : null;
        if (this.laboratory()?.laboratoryId !== lab?.laboratoryId) {
          this.laboratory.set(lab);
          if (lab) {
            this.getExperimentTemplates();
          }
        }
      });

    // Subscribe to the value change on templates per page dropdown
    this.templateTableControls
      .get(this.templatesPerPageField.key)!
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((numOfTemplates) => {
        this.pageSize.set(Number(numOfTemplates));
        this.pageRequested.set(1);
        this.getExperimentTemplates();
      });
  }

  get checkboxesFormArray(): FormArray {
    return this.templateTableControls.get(
      'experimentModuleFilters',
    ) as FormArray;
  }

  initialiseCheckboxes(): void {
    // Reset checkbox form array
    clearFormArray(this.checkboxesFormArray);
    this.experimentModuleFilters.options.forEach(() => {
      const control = this.formBuilder.control(false);
      this.checkboxesFormArray.push(control);
    });
  }

  resetCheckboxes(): void {
    const resetValues = this.checkboxesFormArray.controls.map(() => false);
    this.checkboxesFormArray.patchValue(resetValues);
    this.templateTableControls.markAsPristine();
    this.templateTableControls.markAsUntouched();
  }

  moduleCheckboxClicked(event: Event): void {
    const target = event.target as HTMLFormElement;
    const module = Number.parseInt(target.name) as Module;
    if (target.checked === true) {
      this.selectedModuleList.update((list) => [...list, module]);
    }
    if (target.checked === false) {
      this.selectedModuleList.update((list) =>
        list.filter((x: Module) => x !== module),
      );
    }
  }

  getExperimentTemplates(): void {
    this.fetchTemplateError.set(null);
    this.loading.set(true);

    const experimentMode = this.templateTableControls.get(
      this.experimentModeFilters.key,
    ).value as number;
    const search = this.templateTableControls.get(this.searchField.key)
      .value as string;
    const includeDeleted = this.templateTableControls.get(
      this.includeDeletedTemplatesField.key,
    ).value as boolean;

    this.experimentTemplateService
      .getExperimentTemplates(
        this.laboratory()?.laboratoryId,
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested(),
        experimentMode,
        this.selectedModuleList(),
        search,
        includeDeleted,
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.templates.set(response.templates);
          this.templates().forEach((device: ExperimentTemplateListItem) => {
            orderModules(device.modules);
          });
          if (response.paginationData) {
            this.currentPage.set(response.paginationData.CurrentPage);
            this.pageSize.set(response.paginationData.PageSize);
            this.totalPages.set(response.paginationData.TotalPages);
            this.hasNext.set(response.paginationData.HasNext);
            this.hasPrevious.set(response.paginationData.HasPrevious);
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.fetchTemplateError.set(error);
          this.loading.set(false);
        },
      });
  }

  editExperimentTemplate(templateId: number): void {
    void this.router.navigate([`/experiment/templates/${templateId}/edit`]);
  }

  duplicateExperimentTemplate(templateId: number): void {
    void this.router.navigate(['/experiment/templates/new'], {
      state: { data: templateId },
    });
  }

  deleteTemplateConfirmation(templateId: number): void {
    this.selectedTemplateId.set(templateId);
    this.openModal('delete-template-confirmation');
  }

  deleteExperimentTemplate(): void {
    const templateId = this.selectedTemplateId();
    if (this.processing() || templateId === null) {
      return;
    }
    this.processing.set(true);
    this.deleteTemplateError.set(null);

    this.experimentTemplateService
      .deleteTemplate(templateId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.processing.set(false);
          this.getExperimentTemplates();
          this.closeModal('delete-template-confirmation');
          this.showToast();
        },
        error: (error) => {
          this.deleteTemplateError.set(error);
          this.processing.set(false);
          this.openModal('delete-error');
        },
      });
  }

  showToast(): void {
    this.displayToast.set(true);
    this.successToast = setTimeout(() => {
      this.displayToast.set(false);
    }, 5000);
  }

  useTemplate(templateId: number): void {
    void this.router.navigate(['/experiment/0/set-parameters'], {
      state: { templateId: templateId },
    });
  }

  getPrevious(): void {
    this.pageRequested.set(this.currentPage() - 1);
    this.getExperimentTemplates();
  }

  getNext(): void {
    this.pageRequested.set(this.currentPage() + 1);
    this.getExperimentTemplates();
  }

  onSort(sortTerm: string): void {
    if (this.currentSort().sortBy === sortTerm) {
      this.currentSort.update((sort) => ({
        ...sort,
        sortDirection: sort.sortDirection === 'asc' ? 'desc' : 'asc',
      }));
    } else {
      this.currentSort.set({ sortBy: sortTerm, sortDirection: 'asc' });
    }
    this.getExperimentTemplates();
  }

  onNewExperimentTemplate(): void {
    void this.router.navigate(['/experiment/templates/new']);
  }

  handleSearch(action: string): void {
    switch (action) {
      case 'search':
        this.getExperimentTemplates();
        break;
      case 'clear-search':
        this.templateTableControls.patchValue({ search: null });
        this.getExperimentTemplates();
        break;
    }
  }

  closeModal(modal: string): void {
    this.modalService.close(modal);
  }

  openModal(modal: string): void {
    this.modalService.open(modal);
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'cancel-delete-button':
        this.closeModal('delete-template-confirmation');
        break;
      case 'confirm-delete-button':
        this.deleteExperimentTemplate();
        break;
      case 'continue-button':
        this.getExperimentTemplates();
        this.closeModal('filter');
        break;
      case 'reset-button':
        this.resetForm();
        break;
      case 'error-primary-button':
        this.closeModal('delete-error');
        break;
    }
  }

  resetForm(): void {
    this.resetCheckboxes();
    this.templateTableControls.patchValue({
      experimentModeFilters: null,
      search: null,
      includeDeletedTemplates: false,
    });
    this.getExperimentTemplates();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.successToast) {
      clearTimeout(this.successToast);
    }
  }
}
